import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Receipt,
  Plus,
  Coins,
  CalendarDays,
  Search,
  X,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react'
import client from '../api/client'
import useAuthStore from '../store/useAuthStore'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Card from '../components/ui/Card'
import { useToast } from '../components/ui/Toast'

export default function PersonalFinances() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { user } = useAuthStore()
  
  const [showModal, setShowModal] = useState(false)
  const [pagina, setPagina] = useState(1)
  const [busqueda, setBusqueda] = useState('')
  const limite = 15

  // Formulario de Gasto
  const [form, setForm] = useState({
    descripcion: '',
    monto: '',
    categoria_id: '',
    fecha: new Date().toISOString().split('T')[0]
  })

  // 1. Obtener categorías permitidas para empleado (visible_empleado = 1)
  const { data: categories = [], isLoading: catsLoading } = useQuery({
    queryKey: ['personal-finance-categories'],
    queryFn: async () => {
      const res = await client.get('/finances/categories')
      return res.data?.datos || []
    }
  })

  // 2. Obtener gastos del propio empleado
  const { data: expensesData, isLoading: expensesLoading } = useQuery({
    queryKey: ['personal-finance-expenses', pagina],
    queryFn: async () => {
      const res = await client.get(`/finances/expenses?pagina=${pagina}&limite=${limite}`)
      return res.data?.datos
    }
  })

  const expenses = expensesData?.gastos || []
  const paginacion = expensesData?.paginacion || { total: 0, paginas: 1, pagina_actual: 1 }

  // Filtrar localmente por descripción en la interfaz para mayor fluidez
  const filtrados = expenses.filter(g => 
    g.descripcion.toLowerCase().includes(busqueda.toLowerCase()) ||
    g.categoria_nombre.toLowerCase().includes(busqueda.toLowerCase())
  )

  // Mutación registrar gasto
  const createExpenseMutation = useMutation({
    mutationFn: async (data) => {
      const res = await client.post('/finances/expenses', data)
      return res.data
    },
    onSuccess: () => {
      toast.success('Gasto registrado con éxito')
      setForm({
        descripcion: '',
        monto: '',
        categoria_id: '',
        fecha: new Date().toISOString().split('T')[0]
      })
      setShowModal(false)
      queryClient.invalidateQueries({ queryKey: ['personal-finance-expenses'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.mensaje || 'Error al registrar el gasto')
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.descripcion.trim() || !form.monto || !form.categoria_id) {
      toast.error('Por favor, completa todos los campos obligatorios.')
      return
    }
    if (parseFloat(form.monto) <= 0) {
      toast.error('El monto debe ser mayor a 0.')
      return
    }

    createExpenseMutation.mutate({
      ...form,
      monto: parseFloat(form.monto)
    })
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Receipt className="w-6 h-6 text-cyan" />
            <h1 className="font-display font-bold text-2xl md:text-3xl text-text-primary">
              Caja Chica y Gastos Operativos
            </h1>
          </div>
          <p className="text-text-secondary text-sm">
            Registra y haz seguimiento a los gastos y compras realizados en tus labores de personal.
          </p>
        </div>
        <Button 
          onClick={() => setShowModal(true)} 
          variant="primary" 
          className="flex items-center gap-2 shadow-lg shadow-cyan/10"
        >
          <Plus className="w-4 h-4" />
          <span>Registrar Gasto</span>
        </Button>
      </div>

      {/* Tarjeta Resumen Empleado */}
      <Card className="p-5 bg-gradient-to-r from-navy/30 to-navy-light/10 border-border-default">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan/10 flex items-center justify-center">
              <Coins className="w-5 h-5 text-cyan" />
            </div>
            <div>
              <p className="text-xs text-text-muted font-semibold uppercase tracking-wider">Empleado Activo</p>
              <p className="font-display font-bold text-lg text-text-primary capitalize">
                {user?.nombre} {user?.apellido}
              </p>
            </div>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xs text-text-muted font-semibold uppercase tracking-wider">Gastos Registrados</p>
            <p className="font-display font-bold text-xl text-cyan">
              {paginacion.total} registros
            </p>
          </div>
        </div>
      </Card>

      {/* Listado y Filtros */}
      <Card className="p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
          <h2 className="font-display font-bold text-lg text-text-primary self-start">
            Mi Historial de Egresos
          </h2>
          <div className="relative w-full md:max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Buscar gasto o categoría..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10 pr-4 py-2 bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl text-sm text-text-primary focus:border-cyan focus:outline-none transition-colors w-full"
            />
          </div>
        </div>

        {expensesLoading ? (
          <div className="text-center py-12 text-text-muted font-body">
            Cargando gastos...
          </div>
        ) : filtrados.length === 0 ? (
          <div className="text-center py-12 text-text-muted font-body border border-dashed border-border-subtle rounded-2xl">
            No has registrado ningún gasto operativo aún o no coincide con tu búsqueda.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle text-text-muted text-xs font-semibold uppercase tracking-wider">
                    <th className="text-left pb-3 font-display">Concepto / Descripción</th>
                    <th className="text-left pb-3 font-display">Categoría</th>
                    <th className="text-left pb-3 font-display">Fecha</th>
                    <th className="text-right pb-3 font-display">Monto (Bs)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle text-text-secondary">
                  {filtrados.map((g) => (
                    <tr key={g.id} className="hover:bg-surface-card-hover/40 transition-colors">
                      <td className="py-3.5 pr-4 font-semibold text-text-primary">
                        {g.descripcion}
                      </td>
                      <td className="py-3.5">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan/10 text-cyan border border-cyan/20">
                          {g.categoria_nombre}
                        </span>
                      </td>
                      <td className="py-3.5 text-xs font-mono">
                        {new Date(g.fecha).toLocaleDateString('es-BO', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3.5 text-right font-display font-bold text-text-primary">
                        {parseFloat(g.monto).toFixed(2)} Bs
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {paginacion.paginas > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-border-subtle">
                <span className="text-xs text-text-muted">
                  Página {paginacion.pagina_actual} de {paginacion.paginas}
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    disabled={pagina === 1} 
                    onClick={() => setPagina(prev => Math.max(1, prev - 1))}
                  >
                    Anterior
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    disabled={pagina === paginacion.paginas} 
                    onClick={() => setPagina(prev => Math.min(paginacion.paginas, prev + 1))}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Modal Registrar Gasto */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-navy-dark max-w-md w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default animate-scale-in">
            
            {/* Header */}
            <div className="p-5 border-b border-border-subtle flex justify-between items-center bg-slate-50 dark:bg-navy-dark/40">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-cyan" />
                <h3 className="font-display font-bold text-lg text-text-primary">
                  Registrar Gasto de Caja
                </h3>
              </div>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-1.5 rounded-full hover:bg-slate-200 dark:hover:bg-navy-light text-text-primary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              
              {/* Concepto */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-text-secondary block">
                  Descripción / Concepto *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej. Compra de café y azúcar para staff"
                  value={form.descripcion}
                  onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                />
              </div>

              {/* Monto y Fecha */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Monto (Bs) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    placeholder="0.00"
                    value={form.monto}
                    onChange={(e) => setForm(prev => ({ ...prev, monto: e.target.value }))}
                    className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                  />
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Fecha del Gasto *
                  </label>
                  <input
                    type="date"
                    required
                    value={form.fecha}
                    onChange={(e) => setForm(prev => ({ ...prev, fecha: e.target.value }))}
                    className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors cursor-pointer"
                  />
                </div>
              </div>

              {/* Categoría */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-text-secondary block">
                  Categoría de Gasto *
                </label>
                <select
                  required
                  value={form.categoria_id}
                  onChange={(e) => setForm(prev => ({ ...prev, categoria_id: e.target.value }))}
                  className="w-full bg-white dark:bg-navy-dark border border-border-subtle rounded-xl px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors cursor-pointer"
                >
                  <option value="">Selecciona una categoría</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
                {categories.length === 0 && !catsLoading && (
                  <p className="text-[10px] text-warning flex items-center gap-1 mt-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> No tienes categorías habilitadas para registrar.
                  </p>
                )}
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  loading={createExpenseMutation.isPending}
                >
                  Registrar Gasto
                </Button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  )
}
